import { atom, selector, useRecoilState, useSetRecoilState } from 'recoil';
import { useEffect, useState } from 'react';

export const dialogVisibility = atom<boolean>({
    key: 'dialogVisibility',
    default: false,
});

export const dialogText = atom<string>({
    key: 'dialogText',
    default: '',
});

export const dialogConfirmMode = atom<boolean>({
    key: 'confirmDialogVisibility',
    default: true,
});

export const dialogHeader = atom<string>({
    key: 'dialogHeader',
    default: '',
});

export const dialogButtonsText = atom<{
    accept: string | undefined;
    dismiss: string | undefined;
}>({
    key: 'dialogButtonsText',
    default: {
        accept: undefined,
        dismiss: undefined,
    },
});

export const dialogValue = atom<boolean | null>({
    key: 'dialogValue',
    default: null,
});

interface AppDialogState {
    isVisible: boolean;
    isConfirmMode: boolean;
    text: string;
    header?: string;
    buttonsText: { accept: string | undefined; dismiss: string | undefined };
}

export const dialogState = selector<AppDialogState>({
    key: 'dialogState',
    get: ({ get }) => {
        const isVisible = get(dialogVisibility);
        const isConfirmMode = get(dialogConfirmMode);
        const text = get(dialogText);
        const header = get(dialogHeader);
        const buttonsText = get(dialogButtonsText);

        return {
            isVisible,
            isConfirmMode,
            text,
            header,
            buttonsText,
        };
    },
});

interface PromiseControls {
    res: (data: any) => any;
    rej: (data: any) => any;
    promise: Promise<any>;
}

const createPromise = (): PromiseControls => {
    let res: (data: any) => any;
    let rej: (data: any) => any;

    const promise = new Promise((resolve, reject) => {
        res = resolve;
        rej = reject;
    });

    return {
        res: (data: any) => res(data),
        rej: (data: any) => rej(data),
        promise,
    };
};

export const useAppDialog = () => {
    const setVisibility = useSetRecoilState(dialogVisibility);
    const setConfirmMode = useSetRecoilState(dialogConfirmMode);
    const setText = useSetRecoilState(dialogText);
    const setHeader = useSetRecoilState(dialogHeader);
    const setDialogButtonsText = useSetRecoilState(dialogButtonsText);
    const [value, setValue] = useRecoilState(dialogValue);
    const [promise, setPromise] = useState<PromiseControls | null>();

    useEffect(() => {
        if (value !== null && promise) {
            promise.res(value);

            setPromise(null);
        }
    }, [value]);

    return {
        show(
            text: string,
            {
                header,
            }: {
                header?: string;
            } = {},
        ) {
            setValue(null);
            setVisibility(true);
            setConfirmMode(false);
            setText(text);

            if (header) setHeader(header);

            const p = createPromise();

            setPromise(p);

            return p.promise;
        },
        showConfirm(
            text: string,
            {
                header,
                acceptButtonText,
                dismissButtonText,
            }: {
                header?: string;
                acceptButtonText?: string;
                dismissButtonText?: string;
            } = {},
        ) {
            setValue(null);
            setVisibility(true);
            setConfirmMode(true);
            setText(text);
            setDialogButtonsText({
                dismiss: dismissButtonText,
                accept: acceptButtonText,
            });

            if (header) setHeader(header);

            const p = createPromise();

            setPromise(p);

            return p.promise;
        },
        hide() {
            setVisibility(false);
            setValue(false);
        },
        value,
    };
};
